import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const CATEGORIES = ["jackets", "tees", "jeans", "tote-bags", "tops", "thrifted"];
const CONDITIONS = ["Like New", "Good", "Well-Loved", "Vintage"];

export default function SellThrifts() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", category: "jackets", price: 0, description: "", size: "M",
    condition: "Good", images: [], bargain_enabled: true,
  });
  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <div className="max-w-lg mx-auto p-16 text-center" data-testid="sell-auth-required">
        <h1 className="font-display text-4xl">LOGIN TO SELL</h1>
        <button onClick={() => nav("/login?next=/sell")} className="mt-6 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase">Login</button>
      </div>
    );
  }

  const needsKyc = user.role !== "admin" && user.kyc_status !== "approved";
  if (needsKyc) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="sell-kyc-required">
        <div className="bg-white border-2 border-black brutal-shadow p-6 md:p-10">
          <span className="sticker orange">SELLER GATE</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-none">GET <span className="text-[#FF007F]">VERIFIED</span> TO LIST.</h1>
          <p className="font-mono-b text-sm mt-3 text-black/70">
            We manually verify every seller with a government ID and selfie. Keeps counterfeit off the site and buyers safe.
            Docs stay admin-only. Takes 24-48h once you submit.
          </p>
          <div className="mt-6 border-2 border-black bg-[#F4F4F0] p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-mono-b font-bold text-sm">Current status:
                <span className="ml-2 sticker text-[10px]">{(user.kyc_status || "unsubmitted").toUpperCase()}</span>
              </p>
            </div>
          </div>
          <Link
            to="/account?tab=verification"
            data-testid="sell-kyc-link"
            className="mt-6 inline-block bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase"
          >
            GO TO VERIFICATION
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (f.images.length === 0) { toast.error("Add at least 1 photo"); return; }
    setLoading(true);
    try {
      await api.post("/thrift/submit", { ...f, price: Number(f.price) });
      toast.success("Submitted! We'll review & drop it live.");
      nav("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="sell-page">
      <div className="border-2 border-black brutal-shadow bg-white p-6 md:p-10 relative">
        <span className="sticker orange absolute -top-4 -left-4 rotate-[-4deg]">RE-LOVE</span>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.9]">SELL YOUR <span className="text-[#FF007F]">THRIFTS.</span></h1>
        <p className="font-mono-b mt-3 text-black/70">Fill in your fit. We review it in 24h. If it's fire, we drop it & pay you when it sells.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">ITEM NAME</label>
              <input data-testid="sell-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="brutal-input w-full mt-1" placeholder="e.g. 90s Denim Jacket" />
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">CATEGORY</label>
              <select data-testid="sell-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="brutal-input w-full mt-1 uppercase">
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace("-", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">PRICE (₹)</label>
              <input data-testid="sell-price" required type="number" min={100} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="brutal-input w-full mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">SIZE</label>
              <input data-testid="sell-size" value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} className="brutal-input w-full mt-1" placeholder="M / 32 / One Size" />
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">CONDITION</label>
              <select data-testid="sell-condition" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} className="brutal-input w-full mt-1 uppercase">
                {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">DESCRIPTION</label>
            <textarea data-testid="sell-description" required rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="brutal-input w-full mt-1" placeholder="Tell the story. Brand, era, vibe..." />
          </div>

          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">PHOTOS</label>
            <ImageUpload value={f.images} onChange={(imgs) => setF({ ...f, images: imgs })} max={6} testIdPrefix="sell" />
          </div>

          <label className="flex items-start gap-3 border-2 border-black bg-[#F4F4F0] p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="sell-bargain-enabled"
              checked={f.bargain_enabled}
              onChange={(e) => setF({ ...f, bargain_enabled: e.target.checked })}
              className="mt-1"
            />
            <div>
              <p className="font-mono-b font-bold text-sm uppercase">Allow bargaining</p>
              <p className="font-mono-b text-xs text-black/60 mt-0.5">Buyers can DM you offers below your list price. Turn off if the price is fixed.</p>
            </div>
          </label>

          <button data-testid="sell-submit" disabled={loading} type="submit" className="bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-8 py-3 font-mono-b font-bold uppercase disabled:opacity-50">
            {loading ? "SUBMITTING..." : "SUBMIT FOR REVIEW"}
          </button>
        </form>
      </div>
    </div>
  );
}
